import { Component, ElementRef, EventEmitter, HostListener, Input, OnInit, Output, ViewChild } from "@angular/core";
import { ControlContainer, ControlValueAccessor, FormControl, FormControlDirective, NG_VALUE_ACCESSOR } from "@angular/forms";
import { TranslateService } from "@ngx-translate/core";
import { BaseComponent } from "../../base.component";
import { isEmptyString } from "../../helpers";
import { NotificationBarComponent } from "../../notification-bar";

export interface ErrorConfig {
  priority: number;
  key: string;
  description: string
}
const BASE_TRANSLATION_ROOT = "password-input";

@Component({
  selector: "au-password-input",
  templateUrl: "./password-input.component.html",
  styleUrls: ["./password-input.component.scss"],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: PasswordInputComponent,
      multi: true
    }
  ]
})
export class PasswordInputComponent extends BaseComponent implements ControlValueAccessor, OnInit {

  override componentTranslationRoot = BASE_TRANSLATION_ROOT;
  isCapsLockOn = false;
  hasFocusOnInput = false;
  isInputEmpty = true;
  showPassword = false;
  _formControlName: string = "";
  @ViewChild(FormControlDirective, { static: true })
    formControlDirective!: FormControlDirective;
  @ViewChild("notificationBar", { static: false }) notificationBar!: NotificationBarComponent;
  @ViewChild("input", { static: true }) input!: ElementRef;

  constructor(
    translateService: TranslateService,
    private controlContainer: ControlContainer
  ) {
    super(translateService);

  }
  ngOnInit(): void {
    this.subscriptionsToRelease.push(
      this.control.valueChanges.subscribe(value => {
        this.isInputEmpty = isEmptyString(value);
      })
    );
  }
  @Output() enter = new EventEmitter();
  @Input() label = "";

  @Input() set formControlName(value: string) {
    this._formControlName = value;
  }

  get formControlName(): string {
    return this._formControlName;
  }

  get placeholder(): string {
    return this.translateWithFallback("input-placeholder", BASE_TRANSLATION_ROOT);
  }

  get passwordLabel(): string {
    if (!isEmptyString(this.label)) {
      return this.label;
    }
    return this.translateWithFallback("label", BASE_TRANSLATION_ROOT);
  }

  get control(): FormControl<unknown> {
    return this.controlContainer.control!.get(this.formControlName)! as FormControl;
  }

  get isInValid(): boolean {
    return !this.control.valid && this.control.touched;
  }

  get shouldShowCapsLockWarning(): boolean {
    return (this.isCapsLockOn && !this.isInputEmpty) || (this.hasFocusOnInput && this.isCapsLockOn);
  }

  get validationErrors(): string {
    //even this method will only be called when error object will be defined typescript forces use of ! sign (this.control.errors!)
    //using ! gives unexpected results when running tests so here will make sure to use a non nullable oject
    const errorsObject = this.control.errors ?? {};
    const keys = Object.keys(errorsObject);
    //it will expect to have numbers as keys , but sort will work with strings too
    //sorting will be problematic if we will have  keys greater than 9
    //but that seems not to be the case for this control so  no aditioanl handling is done for that case
    keys.sort((a, b) => a.localeCompare(b));

    return this.translateWithFallback(errorsObject[keys[0]], BASE_TRANSLATION_ROOT);

  }

  @HostListener("window:click", ["$event"])
  onClick(event: KeyboardEvent): void {
    this.updateCapsLockState(event);
  }

  @HostListener("window:keyup", ["$event"])
  onKeyUp(event: KeyboardEvent): void {
    this.updateCapsLockState(event);
  }

  private updateCapsLockState(event: KeyboardEvent): void {
    this.isCapsLockOn = event.getModifierState && event.getModifierState("CapsLock");
    this.handleCapsLockNotification();
  }

  focus() {
    this.input.nativeElement.focus();
  }

  blur() {
    this.input.nativeElement.blur();
  }

  handleCapsLockNotification() {
    if (this.shouldShowCapsLockWarning) {
      this.notificationBar.showWarningMessage({ showIcon: false, text: this.translateWithFallback("caps-lock-on", BASE_TRANSLATION_ROOT) });
    } else {
      this.notificationBar.hide();
    }
  }
  onPasswordInputFocusOut() {
    this.hasFocusOnInput = false;
    this.handleCapsLockNotification();
  }

  onEnter(event: Event) {
    this.enter.emit(event);
  }

  onPasswordInputFocusin() {
    this.hasFocusOnInput = true;
    this.handleCapsLockNotification();
  }
  toogleMainPasswordVisibility() {
    this.showPassword = !this.showPassword;
  }

  get passwordFieldType(): string {
    return this.showPassword ? "text" : "password";
  }
  get title(): string {
    return this.translateWithFallback("hover-text", BASE_TRANSLATION_ROOT);
  }

  registerOnTouched(fn: unknown): void {
    this.formControlDirective.valueAccessor!.registerOnTouched(fn);
  }

  registerOnChange(fn: unknown): void {
    this.formControlDirective.valueAccessor!.registerOnChange(fn);
  }

  writeValue(obj: unknown): void {
    this.formControlDirective.valueAccessor!.writeValue(obj);
  }

}


